import { apiRequest } from './client';
import type {
  CancelOtherSaleInput,
  CostingReport,
  CreateOtherSaleInput,
  CurrentProductCost,
  InventoryTrace,
  SaleCosting,
  CreateOtherSaleProductInput,
  CreateOtherSaleSubcategoryInput,
  OtherSaleCategory,
  OtherSaleCheckoutInput,
  OtherSaleCheckoutResponse,
  OtherSaleDetail,
  OtherSaleListFilters,
  OtherSaleListResponse,
  OtherSaleProduct,
  OtherSaleProductListFilters,
  OtherSaleProductListResponse,
  OtherSaleSubcategory,
  OtherSaleSummary,
  OtherSaleSummaryFilters,
  SetOtherSaleProductActiveResponse,
  UpdateOtherSaleInput,
  UpdateOtherSaleProductInput,
} from '../types/otherSales';

const PATH = '/api/other-sales';

export const createOtherSale = (input: CreateOtherSaleInput) => apiRequest<{ otherSale: OtherSaleDetail }>(PATH, json('POST', input));
export const getOtherSales = (filters: OtherSaleListFilters = {}) => apiRequest<OtherSaleListResponse>(`${PATH}${toQuery(filters)}`);
export const getOtherSale = (id: string) => apiRequest<{ otherSale: OtherSaleDetail }>(`${PATH}/${id}`);
// Fase 22C-1: trazabilidad de inventario de solo lectura; se carga bajo demanda al abrir el detalle.
export const getOtherSaleInventoryTrace = (id: string) => apiRequest<{ inventoryTrace: InventoryTrace }>(`${PATH}/${id}/inventory-trace`);
// Fase 22C-3: costeo histórico + margen (solo roles con financials.viewCosts; backend enforce 403).
export const getOtherSaleCosting = (id: string) => apiRequest<{ costing: SaleCosting }>(`${PATH}/${id}/costing`);
// Fase 22C-4: reportes agregados de costeo (solo financials.viewCosts; backend enforce 403).
export const getOtherSaleCostingReport = (filters: { from: string; to: string; storeId?: string }) => apiRequest<{ report: CostingReport }>(`/api/reports/other-sales/costing-summary${toQuery(filters)}`);
export const getCurrentProductCost = (filters: { productId: string; storeId: string }) => apiRequest<{ currentCost: CurrentProductCost }>(`/api/reports/other-sales/current-product-cost${toQuery(filters)}`);
export const updateOtherSale = (id: string, input: UpdateOtherSaleInput) => apiRequest<{ otherSale: OtherSaleDetail }>(`${PATH}/${id}`, json('PATCH', input));
export const deleteOtherSaleDraft = (id: string) => apiRequest<{ ok: boolean }>(`${PATH}/${id}`, { method: 'DELETE' });
export const confirmOtherSale = (id: string) => apiRequest<{ otherSale: OtherSaleDetail; message: string }>(`${PATH}/${id}/confirm`, { method: 'POST' });
export const cancelOtherSale = (id: string, input: CancelOtherSaleInput) => apiRequest<{ otherSale: OtherSaleDetail; alreadyCancelled: boolean; message: string }>(`${PATH}/${id}/cancel`, json('POST', input));
export const getOtherSaleSummary = (filters: OtherSaleSummaryFilters = {}) => apiRequest<OtherSaleSummary>(`${PATH}/summary${toQuery(filters)}`);
export const getOtherSaleSubcategories = (category: OtherSaleCategory) => apiRequest<{ items: OtherSaleSubcategory[] }>(`${PATH}/subcategories${toQuery({ category })}`);
export const createOtherSaleSubcategory = (input: CreateOtherSaleSubcategoryInput) => apiRequest<{ subcategory: OtherSaleSubcategory }>(`${PATH}/subcategories`, json('POST', input));

// Fase 21C-4A: catálogo de Productos Otros (base del mini-POS). Rutas estáticas del backend
// (21C-2) — nunca llama al checkout, esa parte llega en 21C-4B.
export const listOtherSaleProducts = (filters: OtherSaleProductListFilters = {}) => apiRequest<OtherSaleProductListResponse>(`${PATH}/products${toQuery(filters)}`);
export const getOtherSaleProduct = (id: string) => apiRequest<{ product: OtherSaleProduct }>(`${PATH}/products/${id}`);
export const createOtherSaleProduct = (input: CreateOtherSaleProductInput) => apiRequest<{ product: OtherSaleProduct }>(`${PATH}/products`, json('POST', input));
export const updateOtherSaleProduct = (id: string, input: UpdateOtherSaleProductInput) => apiRequest<{ product: OtherSaleProduct }>(`${PATH}/products/${id}`, json('PATCH', input));
export const setOtherSaleProductActive = (id: string, isActive: boolean) => apiRequest<SetOtherSaleProductActiveResponse>(`${PATH}/products/${id}/activate`, json('PATCH', { isActive }));

// Fase 21C-4B: checkout transaccional del mini-POS. El servidor calcula precio/IVA/totales desde el
// catálogo; el cliente solo manda tienda, método de pago, fecha opcional y líneas (productId+cantidad).
export const checkoutOtherSale = (input: OtherSaleCheckoutInput) => apiRequest<OtherSaleCheckoutResponse>(`${PATH}/checkout`, json('POST', input));

function json(method: string, body: unknown): RequestInit {
  return { method, body: JSON.stringify(body) };
}

function toQuery(filters: object) {
  const query = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => { if (value !== undefined && value !== '') query.set(key, String(value)); });
  // Sin `URLSearchParams.size`: no existe en Chromium 83, el WebView de Android 11 (Bloque G).
  const search = query.toString();
  return search ? `?${search}` : '';
}
